import React from "react";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";

import CustomPagination from "../../../../../core/common/customPagination/CustomPagination";


interface ServerPagination {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

interface PayrollDataTableProps<T> {
  columns: ColumnsType<T>;
  dataSource: T[];
  rowKey: string | ((record: T) => string);
  loading: boolean;
  scrollX?: number;
  emptyText?: string;
  emptyIcon?: string;
  showSrNo?: boolean;
  serverPagination?: ServerPagination;
}

export function PayrollDataTable<T extends object>({
  columns,
  dataSource,
  rowKey,
  loading,
  scrollX = 1100,
  emptyText = "No records found",
  emptyIcon = "ti ti-database-off",
  showSrNo = true,
  serverPagination,
}: PayrollDataTableProps<T>) {
  const srBase = serverPagination
    ? (serverPagination.page - 1) * serverPagination.limit
    : 0;

  const finalColumns: ColumnsType<T> = showSrNo
    ? [
        {
          title: "Sr No.",
          key: "_sr_no",
          width: 80,
          align: "center" as const,
          fixed: "left" as const,
          render: (_: any, __: any, index: number) => (
            <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
              {srBase + index + 1}
            </span>
          ),
        },
        ...columns,
      ]
    : columns;

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-white"
        style={{ minHeight: 320, borderRadius: 12, border: "1px solid #e2e8f0" }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          />
          <p className="mt-3 text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  if (!dataSource.length) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-white"
        style={{ minHeight: 320, borderRadius: 12, border: "1px solid #e2e8f0" }}
      >
        <div className="text-center px-3">
          <i className={emptyIcon} style={{ fontSize: 46, color: "#cbd5e1" }} />
          <p className="text-muted mb-0 mt-3">{emptyText}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white"
      style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0" }}
    >
      <Table<T>
        className="custom-data-table"
        columns={finalColumns}
        dataSource={dataSource}
        rowKey={rowKey as any}
        size="middle"
        pagination={false}
        scroll={{ x: scrollX }}
        rowClassName={(_, index) =>
          index % 2 === 0
            ? "data-table-row-light"
            : "data-table-row-dark"
        }
      />
      {serverPagination && (
        <CustomPagination
          currentPage={serverPagination.page}
          totalPages={Math.max(
            1,
            Math.ceil(serverPagination.total / serverPagination.limit),
          )}
          total={serverPagination.total}
          limit={serverPagination.limit}
          hasNextPage={
            serverPagination.page * serverPagination.limit <
            serverPagination.total
          }
          hasPrevPage={serverPagination.page > 1}
          onPageChange={serverPagination.onPageChange}
          onLimitChange={serverPagination.onLimitChange}
        />
      )}
    </div>
  );
}

export default PayrollDataTable;
